import apiClient from 'utils/ApiClient';

/** Company setting key persisted via PUT /il/settings */
export const DEFAULT_REPORTS_CLICKHOUSE_CONFIG_KEY = 'defaultReportsClickHouseConfigId';

export const REPORTS_STORAGE_KEY_SERVER = 'infralytiqs-reports-active-server';
export const REPORTS_STORAGE_KEY_SUITE = 'infralytiqs-reports-active-suite';

/**
 * Returns the module-config _id marked as the default ClickHouse server for
 * Reports, or null when none is configured or the setting cannot be read.
 */
export async function fetchDefaultReportsClickHouseConfigId(): Promise<string | null> {
  try {
    const res = await apiClient.get(`/il/settings/${DEFAULT_REPORTS_CLICKHOUSE_CONFIG_KEY}`);
    const value = res?.data?.value;
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    return null;
  } catch {
    return null;
  }
}

/** Persist the default ClickHouse server used when Reports has no local history. */
export async function setDefaultReportsClickHouseConfigId(configId: string): Promise<void> {
  await apiClient.put('/il/settings', {
    key: DEFAULT_REPORTS_CLICKHOUSE_CONFIG_KEY,
    value: configId,
    description: 'Default ClickHouse server for the Infralytiqs Reports page',
  });
}

/** Remove the default Reports ClickHouse server setting. */
export async function clearDefaultReportsClickHouseConfigId(): Promise<void> {
  await apiClient.put('/il/settings', {
    key: DEFAULT_REPORTS_CLICKHOUSE_CONFIG_KEY,
    value: null,
    description: 'Default ClickHouse server for the Infralytiqs Reports page',
  });
}

/** True when the user has never chosen a server/suite on the Reports page. */
export function hasReportsLocalSelectionHistory(): boolean {
  return Boolean(
    localStorage.getItem(REPORTS_STORAGE_KEY_SERVER)
    || localStorage.getItem(REPORTS_STORAGE_KEY_SUITE),
  );
}

export interface ReportsModuleConfig {
  _id: string;
  reportSuites?: Array<{ _id: string; name?: string; description?: string }>;
  defaultReportSuiteId?: string;
}

/** Match a suite by name (share/deep-link default) within an already-filtered list. */
export function resolveSuiteIdByName(
  suites: Array<{ _id: string; name?: string }>,
  suiteName?: string | null,
): string | null {
  const wanted = (suiteName || '').trim().toLowerCase();
  if (!wanted) return null;
  const hit = suites.find((s) => (s.name || '').toLowerCase() === wanted);
  return hit?._id || null;
}

/** Pick the report suite to preselect for a ClickHouse server on the Reports page. */
export function resolveReportsSuiteId(
  cfg: ReportsModuleConfig,
  options?: { savedSuiteId?: string | null },
): string | null {
  const suites = cfg.reportSuites ?? [];
  if (suites.length === 0) return null;

  const savedId = options?.savedSuiteId?.trim();
  if (savedId && suites.some((s) => s._id === savedId)) {
    return savedId;
  }

  const defaultId = cfg.defaultReportSuiteId?.trim();
  if (defaultId && suites.some((s) => s._id === defaultId)) {
    return defaultId;
  }

  return suites[0]._id;
}

/** Persist the default report suite for a ClickHouse server configuration. */
export async function setDefaultReportSuiteForConfig(
  configId: string,
  suiteId: string,
): Promise<void> {
  await apiClient.patch(`/il/settings/module-config/${configId}/default-report-suite`, {
    suiteId,
  });
}
